import styles from "./InventoryDetailTags.module.scss";
import { getChildrenCount, pluralizeWithCount, isPaymentActive } from "../../helper";
import Tag from "./components";
import SingleOccupancy from "icons/SingleOccupancy";
import InstantBookingGreen from "icons/InstantBookingGreen";
import OffersBlue from "icons/OffersBlue";
import { getIconName } from "@Pages/PropertyPage/components/RoomTypeSection/utils";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import useIsDesktop from "hooks/useIsDesktop";
import useIsLanguage from "hooks/useIsLanguage";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import PropertyTags from "../PropertyTags";
import config from "@Config/index";
import { iconNameMap } from "../FeatureTags/iconNameMap";
import { shallowEqual, useSelector } from "react-redux";
import { AppState } from "@Store/index";
import {
  resolveAppliedFilterSequence,
  withoutRemovedGroups,
} from "@Pages/Search/utils/appliedFilterSequence";
import {
  getAppliedFilterExcludeKeys,
  getAppliedFilterPills,
  getAppliedFilterReplacedTexts,
  normalizeFilterTagKey,
} from "../../utils/appliedFilterPills";

const ICON_KEYS = Object.keys(iconNameMap);

type InventoryDetailTagsProps = {
  item?: any;
  tags?: any;
  tags_en?: any;
  dataTestId?: string;
  showRatings?: boolean;
  isGridView?: boolean;
  tagMap?: any;
  twoLines?: boolean;
  showCashbackTag?: boolean;
  isScrollable?: boolean;
  promoteAppliedFilters?: boolean;
  /** Recommended cards only: filter groups removed for the card, never shown as applied pills. */
  removedFilterGroups?: string[];
};

const InventoryDetailTags = ({
  item,
  tags,
  tags_en,
  dataTestId,
  showRatings = false,
  isGridView = false,
  twoLines = false,
  showCashbackTag = false,
  tagMap = {},
  isScrollable = false,
  promoteAppliedFilters = false,
  removedFilterGroups,
}: InventoryDetailTagsProps): JSX.Element => {
  const roomOptionsCount = getChildrenCount(item);
  const hasInstantBooking = !!item && isPaymentActive(item);
  const isDesktop = useIsDesktop();
  const { t } = useTranslationHook();
  const language = useIsLanguage();
  const offerSuffix = language?.startsWith("zh") ? "" : "s";
  const { filters, appliedFilterSequence } = useSelector(
    ({ searchPageData }: AppState) => ({
      filters: searchPageData.filters,
      appliedFilterSequence: searchPageData.appliedFilterSequence,
    }),
    shallowEqual,
  );

  function getUniqueTagIndices(tagArrays: any): number[] {
    try {
      const keptIndices: number[] = [];
      const kept: Array<string> = [];

      tagArrays?.forEach((tag: any, idx: number) => {
        if (!tag || typeof tag !== "string") return;
        const tagLower = tag.toLowerCase();
        const tagKey = ICON_KEYS.find((k) => tagLower.split(" ").includes(k)) || tagLower;

        const isDuplicate = kept.some((existingTag) => {
          const existingLower = existingTag.toLowerCase();
          const existingKey =
            ICON_KEYS.find((k) => existingLower.split(" ").includes(k)) || existingLower;
          return (
            existingKey === tagKey ||
            tagLower.includes(existingLower) ||
            existingLower.includes(tagLower)
          );
        });

        if (!isDuplicate) {
          kept.push(tag);
          keptIndices.push(idx);
        }
      });

      return keptIndices;
    } catch {
      return Array.isArray(tagArrays) ? tagArrays.map((_: any, i: number) => i) : [];
    }
  }

  const keptIndices = getUniqueTagIndices(tags_en?.length ? tags_en : tags);
  const uniqueTags: any = keptIndices.map((i: number) => tags?.[i]);
  const uniqueTagsEn: any = keptIndices.map((i: number) => tags_en?.[i] ?? tags?.[i]);

  return (
    <div
      className={formatClassNames(
        styles.tagsContainer,
        tags && styles.tags,
        isDesktop && !isGridView && styles.desktop,
        showRatings && styles.ratings,
        twoLines && styles.twoLines,
        isScrollable && styles.scrollable,
        isGridView && styles.gridView,
      )}
    >
      {(() => {
        const offersTag = item?.offers_count ? (
          <Tag
            icon={<OffersBlue />}
            text={pluralizeWithCount({
              noun: t("searchpage.components.searchlistitem.inventorydetailtags.offer"),
              count: item?.offers_count,
              suffix: offerSuffix,
            })}
            dataTestId={dataTestId}
            key="offers"
            variant="offer"
          />
        ) : null;

        const instantBookingTag = hasInstantBooking ? (
          <Tag
            icon={<InstantBookingGreen height="14px" width="14px" />}
            text={t("searchpage.components.searchlistitem.inventorydetailtags.instantbooking")}
            dataTestId={dataTestId}
            key="instant"
            variant="instant"
          />
        ) : null;

        const roomOptionsTag =
          roomOptionsCount !== 0 ? (
            <Tag
              icon={<SingleOccupancy height="16px" width="16px" />}
              text={pluralizeWithCount({
                noun: t("searchpage.components.searchlistitem.inventorydetailtags.roomoption"),
                count: roomOptionsCount,
                suffix: t(
                  "searchpage.components.searchlistitem.inventorydetailtags.roomoptionsuffix",
                ),
              })}
              dataTestId={dataTestId}
              key="roomOptions"
            />
          ) : null;

        const processedTags = (uniqueTags || []).map((it: string, index: number) => {
          const tag =
            (config.IS_CHINA_APP ? uniqueTagsEn?.[index] : uniqueTags?.[index]) || tagMap[it] || it;
          let variant = "default";
          const lowerIt = it.toLowerCase();
          if (
            lowerIt.includes("pay in installment") ||
            lowerIt.includes("pay in instalment") ||
            lowerIt.includes("installments") ||
            lowerIt.includes("instalments") ||
            lowerIt.includes("no visa no pay")
          ) {
            variant = "highlighted";
          }
          return {
            element: (
              <Tag
                icon={getIconName(tag, "16px", "16px")}
                text={it}
                key={`tag-${index}`}
                dataTestId={dataTestId}
                variant={variant as any}
              />
            ),
            originalText: it,
            variant,
          };
        });

        const highlightedObjs = processedTags.filter(
          (tagObj: any) => tagObj.variant === "highlighted",
        );
        const otherObjs = processedTags.filter((tagObj: any) => tagObj.variant !== "highlighted");

        const sequence = promoteAppliedFilters
          ? withoutRemovedGroups(
              resolveAppliedFilterSequence(appliedFilterSequence, filters),
              removedFilterGroups,
            )
          : [];
        const appliedPills = getAppliedFilterPills({
          sequence,
          uniqueTags,
          uniqueTagsEn,
          activeRoomTypes: item?.active_room_types,
          item,
        });
        const excludeKeys = getAppliedFilterExcludeKeys(appliedPills);
        const replacedTexts = getAppliedFilterReplacedTexts(appliedPills);
        const isPromotedTag = (text: string) =>
          replacedTexts.has(text) || excludeKeys.has(normalizeFilterTagKey(text));

        // Sort highlighted: Pay in installments first, then No Visa No Pay
        highlightedObjs.sort((a: any, b: any) => {
          const isPayA = a.originalText.toLowerCase().includes("pay in");
          const isPayB = b.originalText.toLowerCase().includes("pay in");
          if (isPayA && !isPayB) return -1;
          if (!isPayA && isPayB) return 1;
          return 0;
        });

        const highlightedTags = highlightedObjs
          .filter((tagObj: any) => !isPromotedTag(tagObj.originalText))
          .map((tagObj: any) => tagObj.element);
        const otherTags = otherObjs
          .filter((tagObj: any) => !isPromotedTag(tagObj.originalText))
          .map((tagObj: any) => tagObj.element);

        const appliedTags = appliedPills.map((pill) => {
          const isTranslatedLabel =
            pill.source === "roomType" || pill.source === "cancellationPolicy";
          return (
            <Tag
              icon={getIconName(pill.iconKey, "16px", "16px")}
              text={isTranslatedLabel ? t(pill.label) : pill.label}
              key={`applied-${pill.source}-${pill.value}`}
              dataTestId={dataTestId}
              // Keeps No Visa No Pay's highlighted look in its applied position (P1D-5528).
              variant={pill.source === "cancellationPolicy" ? "highlighted" : undefined}
            />
          );
        });

        const allTagElements: JSX.Element[] = [];

        if (offersTag) allTagElements.push(offersTag);
        if (instantBookingTag) allTagElements.push(instantBookingTag);

        // Offers -> Instant -> applied-filter pills -> Highlighted -> Room Options -> Others
        allTagElements.push(...appliedTags);
        allTagElements.push(...highlightedTags);
        if (roomOptionsTag) allTagElements.push(roomOptionsTag);
        allTagElements.push(...otherTags);

        let cacheBackTag = null;
        if (showCashbackTag) {
          cacheBackTag = (
            <PropertyTags
              inventory={item}
              showCashbackValue
              className={styles.propertyTag}
              showCashbackIfOtherPropertyTagsDontHave={isGridView || !isDesktop}
              key="cashback"
            />
          );
        }

        if (isScrollable && (isGridView || !isDesktop)) {
          const evenTags = allTagElements.filter(Boolean).filter((_, idx) => idx % 2 === 0);
          const oddTags = allTagElements.filter(Boolean).filter((_, idx) => idx % 2 === 1);
          return (
            <div className={styles.scrollTwoRows}>
              <div className={styles.scrollRow}>
                {cacheBackTag}
                {evenTags}
              </div>
              <div className={styles.scrollRow}>{oddTags}</div>
            </div>
          );
        }

        return [cacheBackTag, ...allTagElements];
      })()}
    </div>
  );
};

export default wrapperHOC(InventoryDetailTags, {
  componentName: "InventoryDetailTags-SearchListItemDesktop-SearchPage",
  showForChina: true,
});
